import { useState } from 'react';
import { AlertOctagon, Loader2, MapPin, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { sosApi } from '../services/api';

/**
 * Floating Emergency SOS button (worker view).
 * Two-step confirmation prevents accidental triggers.
 * Captures GPS via the browser, sends to supervisors instantly.
 */
export function SosButton({ clientId }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('confirm'); // confirm | sending | sent
  const [note, setNote] = useState('');
  const [coords, setCoords] = useState(null);

  const handleOpen = () => {
    setStep('confirm');
    setNote('');
    setCoords(null);
    setOpen(true);
  };

  const fireSos = async () => {
    setStep('sending');
    // Try to grab location (timeout 8s) — proceed without it if it fails
    const getLoc = () => new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({
          latitude: p.coords.latitude,
          longitude: p.coords.longitude,
          accuracy_m: p.coords.accuracy,
        }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
      );
    });
    const loc = await getLoc();
    setCoords(loc);
    try {
      await sosApi.trigger({
        ...(loc || {}),
        note: note || null,
        client_id: clientId || null,
      });
      setStep('sent');
      toast.success('🚨 SOS sent — your supervisors have been alerted');
    } catch (err) {
      setStep('confirm');
      toast.error(err.response?.data?.detail || 'Failed to send SOS — call 000 if this is life-threatening');
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        type="button"
        onClick={handleOpen}
        className="fixed bottom-6 right-6 z-50 h-16 w-16 rounded-full bg-red-600 hover:bg-red-700 text-white shadow-2xl ring-4 ring-red-200 flex items-center justify-center transition-all hover:scale-110 group"
        data-testid="sos-button"
        aria-label="Emergency SOS"
        title="Emergency SOS"
      >
        <span className="absolute inset-0 rounded-full bg-red-500/50 animate-ping" aria-hidden="true" />
        <AlertOctagon className="h-7 w-7 relative" />
        <span className="absolute -top-1 -right-1 bg-white text-red-700 text-[10px] font-bold rounded-full px-1.5 py-0.5 shadow border border-red-200">SOS</span>
      </button>

      {/* Confirmation dialog */}
      <Dialog open={open} onOpenChange={(o) => { if (!o && step !== 'sending') setOpen(o); }}>
        <DialogContent className="max-w-md border-2 border-red-300">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertOctagon className="h-6 w-6" />
              Emergency SOS
            </DialogTitle>
          </DialogHeader>

          {step === 'confirm' && (
            <div className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-900">
                <p className="font-semibold mb-1">Use this only in an emergency.</p>
                <p>If anyone is in immediate danger, call <strong>000</strong> first.</p>
                <p className="mt-2">Triggering this will notify all supervisors and your organisation with your current GPS location.</p>
              </div>
              <div>
                <Label className="text-sm">Quick note (optional)</Label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="What's happening? E.g. behaviour escalation, injury, unsafe situation..."
                  rows={3}
                  maxLength={500}
                  data-testid="sos-note"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  onClick={fireSos}
                  data-testid="confirm-sos-btn"
                >
                  <AlertOctagon className="h-4 w-4 mr-1" />
                  Send SOS Alert
                </Button>
              </div>
            </div>
          )}

          {step === 'sending' && (
            <div className="py-8 flex flex-col items-center gap-3">
              <Loader2 className="h-10 w-10 text-red-600 animate-spin" />
              <p className="font-medium text-slate-800">Capturing your location...</p>
              <p className="text-sm text-slate-500">Please allow location access if prompted.</p>
            </div>
          )}

          {step === 'sent' && (
            <div className="py-4 space-y-3 text-center" data-testid="sos-sent">
              <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
                <AlertOctagon className="h-7 w-7 text-emerald-600" />
              </div>
              <p className="font-semibold text-slate-900">Alert sent — help is on the way</p>
              <p className="text-sm text-slate-500">
                Your supervisors have been notified by in-app alert and email.
                {coords ? ' Your location was shared.' : ' Location could not be captured — make sure to phone them too.'}
              </p>
              {coords && (
                <a
                  href={`https://www.google.com/maps?q=${coords.latitude},${coords.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-teal-600 hover:underline"
                >
                  <MapPin className="h-3.5 w-3.5" /> View location
                </a>
              )}
              <Button onClick={() => setOpen(false)} className="w-full mt-3 bg-slate-700 hover:bg-slate-800">
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
